import reduceReducers from 'reduce-reducers';
import { combineReducers, Reducer } from 'redux';

import { ActionType } from '../../../store/actionTypes';
import {
  ActionWithPlayload,
  EntityAction,
  ErrorAction,
  isActionWithPayload,
  Action as PlainAction,
} from '../../../store/types';
import { CollectionState, createCollectionReducer } from '../shared/collection';

interface ExperimentByVariableState {
  [variableName: string]: CollectionState;
}

interface TemplateWithAttributeState {
  [templateName: string]: { isFeching: boolean; error?: string };
}

interface CampaignsFormDataState {
  experimentsByVariable: ExperimentByVariableState;
  triggerEvents: CollectionState;
  templates: CollectionState;
  // Loading state of each individual template with attributes
  templateWithAttributes: TemplateWithAttributeState;
}

interface CampaignCreateState {
  isCreating: boolean;
  error?: string;
}

interface CampaignUpdateState {
  [name: string]: {
    isUpdating: boolean;
    error?: string;
  };
}

interface CampaignFetchState {
  [name: string]: {
    isFetching: boolean;
    error?: string;
  };
}

export interface CampaignsState {
  view: CollectionState;
  form: CampaignsFormDataState;
  create: CampaignCreateState;
  update: CampaignUpdateState;
  fetch: CampaignFetchState;
}

const campaignCollectionReducer = createCollectionReducer({
  started: ActionType.FETCH_CAMPAIGN_LIST_STARTED,
  succeeded: ActionType.FETCH_CAMPAIGN_LIST_SUCCEEDED,
  failed: ActionType.FETCH_CAMPAIGN_LIST_FAILED,
});

const campaignCollectionExtendedReducer = function (
  state: CollectionState,
  action:
    | EntityAction<ActionType.CREATE_CAMPAIGN_SUCCEEDED>
    | EntityAction<ActionType.UPDATE_CAMPAIGN_SUCCEEDED>,
): CollectionState {
  if (action.type === ActionType.CREATE_CAMPAIGN_SUCCEEDED) {
    return {
      ...state,
      items: [action.payload.result as string, ...state.items],
    };
  }

  if (action.type === ActionType.UPDATE_CAMPAIGN_SUCCEEDED) {
    return {
      ...state,
      items: state.items.filter((name) => name !== action.payload.campaignName),
    };
  }

  return state;
};

const campaignViewReducer: Reducer<CollectionState> = reduceReducers(
  campaignCollectionReducer,
  campaignCollectionExtendedReducer,
);

type ExperimentsByVariableActions =
  | PlainAction<ActionType.FETCH_EXPERIMENTS_BY_VARIABLE_STARTED>
  | EntityAction<ActionType.FETCH_EXPERIMENTS_BY_VARIABLE_SUCCEEDED>
  | ErrorAction<ActionType.FETCH_EXPERIMENTS_BY_VARIABLE_FAILED>;

const experimentCollectionReducer = createCollectionReducer({
  started: ActionType.FETCH_EXPERIMENTS_BY_VARIABLE_STARTED,
  succeeded: ActionType.FETCH_EXPERIMENTS_BY_VARIABLE_SUCCEEDED,
  failed: ActionType.FETCH_EXPERIMENTS_BY_VARIABLE_FAILED,
});

const experimentsByVariableReducer: Reducer<
  ExperimentByVariableState,
  ExperimentsByVariableActions
> = function (
  state: ExperimentByVariableState = {},
  action: ExperimentsByVariableActions,
) {
  // variableName is appended to the fetch request action and
  // api middleware add any additional property in requestion action
  // to subsequent generated actions
  if (isActionWithPayload(action)) {
    const variableName = action.payload.variableName;
    if (variableName) {
      return {
        ...state,
        [variableName]: experimentCollectionReducer(
          state[variableName],
          action,
        ),
      };
    }
  }

  return state;
};

const triggerEventsReducer = createCollectionReducer({
  started: ActionType.FETCH_TRIGGER_EVENTS_STARTED,
  succeeded: ActionType.FETCH_TRIGGER_EVENTS_SUCCEEDED,
  failed: ActionType.FETCH_TRIGGER_EVENTS_FAILED,
});

const templateCollectionReducer = createCollectionReducer({
  started: ActionType.FETCH_TEMPLATES_LIST_STARTED,
  succeeded: ActionType.FETCH_TEMPLATES_LIST_SUCCEEDED,
  failed: ActionType.FETCH_TEMPLATES_LIST_FAILED,
});

type TemplateWithAttributeActions =
  | ActionWithPlayload<ActionType.FETCH_TEMPLATE_STARTED>
  | EntityAction<ActionType.FETCH_TEMPLATE_SUCCEEDED>
  | ErrorAction<ActionType.FETCH_TEMPLATE_FAILED>;

const templateWithAttributesReducer: Reducer<
  TemplateWithAttributeState,
  TemplateWithAttributeActions
> = function (
  state: TemplateWithAttributeState = {},
  action: TemplateWithAttributeActions,
): TemplateWithAttributeState {
  if (isActionWithPayload(action)) {
    const templateId = action.payload.templateId;

    if (templateId) {
      switch (action.type) {
        case ActionType.FETCH_TEMPLATE_STARTED:
          return {
            ...state,
            [templateId]: {
              isFeching: true,
              error: '',
            },
          };
        case ActionType.FETCH_TEMPLATE_SUCCEEDED:
          return {
            ...state,
            [templateId]: {
              isFeching: false,
              error: '',
            },
          };
        case ActionType.FETCH_TEMPLATE_FAILED:
          return {
            ...state,
            [templateId]: {
              isFeching: false,
              error: action.payload.message,
            },
          };
      }
    }
  }

  return state;
};

const campaignFormReducer: Reducer<CampaignsFormDataState> =
  combineReducers<CampaignsFormDataState>({
    experimentsByVariable: experimentsByVariableReducer,
    triggerEvents: triggerEventsReducer,
    templates: templateCollectionReducer,
    templateWithAttributes: templateWithAttributesReducer,
  });

type CampaignCreateActions =
  | PlainAction<ActionType.CREATE_CAMPAIGN_STARTED>
  | EntityAction<ActionType.CREATE_CAMPAIGN_SUCCEEDED>
  | ErrorAction<ActionType.CREATE_CAMPAIGN_FAILED>;

const campaignCreateReducer: Reducer<
  CampaignCreateState,
  CampaignCreateActions
> = function (
  state: CampaignCreateState = { isCreating: false },
  action: CampaignCreateActions,
): CampaignCreateState {
  switch (action.type) {
    case ActionType.CREATE_CAMPAIGN_STARTED:
      return {
        ...state,
        isCreating: true,
        error: '',
      };
    case ActionType.CREATE_CAMPAIGN_SUCCEEDED:
      return {
        ...state,
        isCreating: false,
      };
    case ActionType.CREATE_CAMPAIGN_FAILED:
      return {
        isCreating: false,
        error: action.payload.message,
      };
    default:
      return state;
  }
};

type CampaignUpdateActions =
  | ActionWithPlayload<ActionType.UPDATE_CAMPAIGN_STARTED>
  | EntityAction<ActionType.UPDATE_CAMPAIGN_SUCCEEDED>
  | ErrorAction<ActionType.UPDATE_CAMPAIGN_FAILED>;

const campaignUpdateReducer: Reducer<
  CampaignUpdateState,
  CampaignUpdateActions
> = function (
  state: CampaignUpdateState = {},
  action: CampaignUpdateActions,
): CampaignUpdateState {
  if (isActionWithPayload(action)) {
    const campaignName = action.payload.campaignName;
    switch (action.type) {
      case ActionType.UPDATE_CAMPAIGN_STARTED:
        return {
          ...state,
          [campaignName]: {
            isUpdating: true,
            error: '',
          },
        };
      case ActionType.UPDATE_CAMPAIGN_SUCCEEDED:
        return {
          ...state,
          [campaignName]: {
            isUpdating: false,
            error: '',
          },
        };
      case ActionType.UPDATE_CAMPAIGN_FAILED:
        return {
          ...state,
          [campaignName]: {
            isUpdating: false,
            error: action.payload.message,
          },
        };
    }
  }

  return state;
};

type CampaignFetchActions =
  | ActionWithPlayload<ActionType.FETCH_CAMPAIGN_STARTED>
  | EntityAction<ActionType.FETCH_CAMPAIGN_SUCCEEDED>
  | ErrorAction<ActionType.FETCH_CAMPAIGN_FAILED>;

const campaignFetchReducer: Reducer<CampaignFetchState, CampaignFetchActions> =
  function (
    state: CampaignFetchState = {},
    action: CampaignFetchActions,
  ): CampaignFetchState {
    if (isActionWithPayload(action)) {
      const campaignName = action.payload.campaignName;
      switch (action.type) {
        case ActionType.FETCH_CAMPAIGN_STARTED:
          return {
            ...state,
            [campaignName]: {
              isUpdating: true,
              error: '',
            },
          };
        case ActionType.FETCH_CAMPAIGN_SUCCEEDED:
          return {
            ...state,
            [campaignName]: {
              isUpdating: false,
              error: '',
            },
          };
        case ActionType.FETCH_CAMPAIGN_FAILED:
          return {
            ...state,
            [campaignName]: {
              isUpdating: false,
              error: action.payload.message,
            },
          };
      }
    }

    return state;
  };

const campaignsReducer: Reducer<CampaignsState> =
  combineReducers<CampaignsState>({
    view: campaignViewReducer,
    form: campaignFormReducer,
    create: campaignCreateReducer,
    update: campaignUpdateReducer,
    fetch: campaignFetchReducer,
  });

export default campaignsReducer;
